<template>
  <div class="subscription-purchase" :class="{ 'is-embedded': embedded, 'is-mobile': isMobileView }">
    <MobileSubscriptionPurchase
      v-if="isMobileView"
      :user-avatar="userAvatar"
      :user-nickname="userNickname"
      :user-email="userEmail"
      :membership-expiry-text="membershipExpiryText"
      :current-membership-label="currentMembershipLabel"
      :purchase-footnote="purchaseFootnote"
      :purchase-action-label="purchaseActionLabel"
      :version-plans="versionPlans"
      :available-pricing-plans="availablePricingPlans"
      :fuel-packages="fuelPackages"
      :embedded="embedded"
      :is-loading="isLoading"
      :is-creating-order="isCreatingOrder"
      :is-domestic="isDomestic"
      :can-manage-subscription="canManageSubscription"
      :can-submit-plan-purchase="canSubmitPlanPurchase"
      :show-price-quote="showPriceQuote"
      :price-quote="priceQuote"
      :price-quote-loading="priceQuoteLoading"
      :price-quote-error="priceQuoteError"
      :has-current-price-quote="hasCurrentPriceQuote"
      :payment-channels-loading="paymentChannelsLoading"
      :fuel-packages-visible="fuelPackagesVisible"
      :fuel-packages-loading="fuelPackagesLoading"
      :format-number="formatNumber"
      :format-point-range="formatPointRange"
      :visible-features="visibleFeatures"
      :period-name="periodName"
      :format-package-price="formatPackagePrice"
      :get-pricing-subline="getPricingSubline"
      :points-data="pointsData"
      :selected-plan-data="selectedPlanData"
      :selected-pricing-plan="selectedPricingPlan"
      :selected-plan="selectedPlan"
      :selected-period-type="selectedPeriodType"
      :fuel-purchase-loading="fuelPurchaseLoading"
      @close="close"
      @toggle-fuel="openFuelPackages"
      @select-plan="selectPlan"
      @select-period="setPeriodType"
      @purchase-plan="handlePlanPurchase"
      @purchase-fuel="purchaseFuelPackage"
      @retry-price-quote="fetchPriceQuote"
    />
    <template v-else>
    <header class="member-header">
      <div class="member-profile">
        <div class="member-avatar-column">
          <el-avatar :size="embedded ? 42 : 48" :src="userAvatar" />
          <span v-if="membershipExpiryText" class="member-expiry">{{ membershipExpiryText }}</span>
        </div>
        <div class="member-identity">
          <div class="member-name-row">
            <strong class="member-name">{{ userNickname }}</strong>
            <el-tag size="small" effect="plain" type="primary">{{ currentMembershipLabel }}</el-tag>
          </div>
          <el-tooltip v-if="userEmail" :content="userEmail" placement="bottom" :show-after="500">
            <span class="member-email">{{ userEmail }}</span>
          </el-tooltip>
          <span v-else class="member-email">{{ $t('views.account_center.purchase_account_fallback') }}</span>
        </div>
      </div>

      <div class="member-header-actions">
        <div class="points-summary">
          <div class="points-summary-total">
            <span>{{ $t('views.account_center.total_points') }}</span>
            <strong>{{ formatNumber(pointsData.totalPoints) }}</strong>
          </div>
          <div class="points-summary-breakdown">
            <span>{{ $t('views.account_center.subscription_points') }} {{ formatPointRange(pointsData.subPoints, pointsData.subTotal) }}</span>
            <span>{{ $t('views.account_center.purchased_points') }} {{ formatPointRange(pointsData.buyPoints, pointsData.buyTotal) }}</span>
            <span>{{ $t('views.account_center.activity_points') }} {{ formatPointRange(pointsData.actPoints, pointsData.actTotal) }}</span>
          </div>
        </div>
        <el-button class="fuel-button" type="primary" plain @click="openFuelPackagesDialog">
          <el-icon><Coin /></el-icon>
          {{ $t('views.account_center.purchase_fuel_packages') }}
        </el-button>
        <el-button
          v-if="!embedded"
          class="purchase-close"
          circle
          text
          :aria-label="$t('common.close')"
          @click="close"
        >
          <el-icon><Close /></el-icon>
        </el-button>
      </div>
    </header>

    <section class="purchase-main">
      <el-alert
        v-if="!canManageSubscription"
        class="subscription-owner-notice"
        type="info"
        :closable="false"
        :title="$t('views.account_center.purchase_owner_only')"
      />
      <div v-if="isLoading" class="subscription-loading" aria-busy="true">
        <el-skeleton animated :rows="8" />
      </div>

      <div v-else-if="versionPlans.length" class="subscription-layout">
        <aside class="benefits-panel">
          <div class="benefits-kicker">{{ $t('views.account_center.purchase_benefits_kicker') }}</div>
          <h2>{{ selectedPlanData?.ver_name }}</h2>
          <p class="benefits-description">{{ selectedPlanData?.desc }}</p>
          <div class="benefits-rule" />
          <ul class="benefits-list">
            <li v-for="(feature, index) in visibleFeatures(selectedPlanData)" :key="index">
              <span class="benefit-index">{{ String(index + 1).padStart(2, '0') }}</span>
              <span>{{ feature }}</span>
            </li>
          </ul>
          <div class="benefits-footer">
            <el-icon><Lock /></el-icon>
            <span>{{ purchaseFootnote }}</span>
          </div>
        </aside>

        <div class="subscription-selection">
          <div class="selection-heading">
            <div>
              <div class="section-eyebrow">{{ $t('views.account_center.purchase_title') }}</div>
              <p>{{ $t('views.account_center.purchase_subtitle') }}</p>
            </div>
            <div class="points-inline">
              <span>{{ $t('views.account_center.total_points') }}</span>
              <strong>{{ formatNumber(pointsData.totalPoints) }}</strong>
            </div>
          </div>

          <div class="version-tabs" role="tablist" :aria-label="$t('views.account_center.purchase_version_label')">
            <button
              v-for="plan in versionPlans"
              :key="plan.ver_id"
              type="button"
              role="tab"
              :aria-selected="selectedPlan === plan.ver_id"
              :class="{ active: selectedPlan === plan.ver_id }"
              @click="selectPlan(plan.ver_id)"
            >
              <span>{{ plan.ver_name }}</span>
            </button>
          </div>

          <div class="period-heading">
            <span>{{ $t('views.account_center.purchase_billing_label') }}</span>
            <small>{{ $t('views.account_center.purchase_period_hint') }}</small>
          </div>
          <div
            class="period-carousel"
            :class="{ 'has-navigation': showPeriodNavigation }"
            :style="{ '--period-card-width': `${periodCardWidth}px` }"
          >
            <button
              v-if="showPeriodNavigation"
              class="period-carousel-arrow period-carousel-arrow-prev"
              type="button"
              :disabled="!canScrollPeriodsPrev"
              :aria-label="$t('views.account_center.purchase_previous_plan')"
              @click="scrollPeriods(-1)"
            >
              <el-icon><ArrowLeft /></el-icon>
            </button>

            <div
              ref="periodViewport"
              class="period-carousel-viewport"
              role="radiogroup"
              :aria-label="$t('views.account_center.purchase_billing_label')"
              @scroll="handlePeriodScroll"
            >
              <div class="period-cards">
                <button
                  v-for="pricing in availablePricingPlans"
                  :key="pricing.plan_id"
                  type="button"
                  role="radio"
                  :aria-checked="selectedPeriodType === Number(pricing.period_type)"
                  class="period-card"
                  :class="{ active: selectedPeriodType === Number(pricing.period_type), recommended: pricing.recommend === 1 }"
                  @click="setPeriodType(Number(pricing.period_type))"
                >
                  <span v-if="pricing.recommend === 1" class="period-badge">{{ $t('views.account_center.most_popular') }}</span>
                  <span class="period-name">{{ periodName(pricing.period_type) }}</span>
                  <strong>{{ formatPackagePrice(pricing.price, pricing.currency) }}</strong>
                  <span class="period-subline">{{ getPricingSubline(pricing) }}</span>
                  <del v-if="pricing.original_price && Number(pricing.original_price) > Number(pricing.price)">{{ formatPackagePrice(pricing.original_price, pricing.currency) }}</del>
                </button>
              </div>
            </div>

            <button
              v-if="showPeriodNavigation"
              class="period-carousel-arrow period-carousel-arrow-next"
              type="button"
              :disabled="!canScrollPeriodsNext"
              :aria-label="$t('views.account_center.purchase_next_plan')"
              @click="scrollPeriods(1)"
            >
              <el-icon><ArrowRight /></el-icon>
            </button>
          </div>

          <section v-if="showPriceQuote" class="purchase-price-quote" aria-live="polite" :aria-busy="priceQuoteLoading">
            <dl :aria-hidden="priceQuoteError">
              <div>
                <dt>{{ $t('views.account_center.purchase_plan_price') }}</dt>
                <dd><span v-if="hasCurrentPriceQuote && !priceQuoteLoading">{{ formatPackagePrice(priceQuote.price, priceQuote.currency) }}</span><el-skeleton-item v-else-if="!priceQuoteError" variant="text" class="quote-amount-skeleton" aria-hidden="true" /><span v-else>—</span></dd>
              </div>
              <div>
                <dt>{{ $t('views.account_center.purchase_credit') }}</dt>
                <dd><span v-if="hasCurrentPriceQuote && !priceQuoteLoading">-{{ formatPackagePrice(Number(priceQuote.discount).toFixed(2), priceQuote.currency) }}</span><el-skeleton-item v-else-if="!priceQuoteError" variant="text" class="quote-amount-skeleton" aria-hidden="true" /><span v-else>—</span></dd>
              </div>
              <div class="price-quote-total">
                <dt>{{ $t('views.account_center.purchase_payable') }}</dt>
                <dd><span v-if="hasCurrentPriceQuote && !priceQuoteLoading">{{ formatPackagePrice(priceQuote.amount, priceQuote.currency) }}</span><el-skeleton-item v-else-if="!priceQuoteError" variant="text" class="quote-amount-skeleton" aria-hidden="true" /><span v-else>—</span></dd>
              </div>
            </dl>
            <small :aria-hidden="priceQuoteError">{{ $t('views.account_center.purchase_quote_note') }}</small>
            <div v-if="priceQuoteError" class="price-quote-error">
              <span>{{ $t('views.account_center.purchase_quote_failed') }}</span>
              <el-button text type="primary" @click="fetchPriceQuote">{{ $t('views.account_center.purchase_quote_retry') }}</el-button>
            </div>
          </section>

          <div v-if="selectedPricingPlan && canManageSubscription" class="purchase-summary">
            <div>
              <span>{{ $t('views.account_center.purchase_selected_plan') }}</span>
              <strong>{{ selectedPlanData.ver_name }} · {{ periodName(selectedPricingPlan.period_type) }}</strong>
            </div>
            <el-button
              type="primary"
              :disabled="!canSubmitPlanPurchase"
              :loading="isCreatingOrder || priceQuoteLoading"
              :aria-busy="isCreatingOrder || priceQuoteLoading"
              @click="handlePlanPurchase(selectedPlanData)"
            >
              {{ purchaseActionLabel }}
            </el-button>
          </div>

          <section class="fuel-section">
            <button type="button" class="fuel-section-toggle" :aria-expanded="fuelInlineVisible" @click="toggleFuelInline">
              <span><el-icon><Coin /></el-icon>{{ $t('views.account_center.purchase_fuel_packages') }}</span>
              <small>{{ $t('views.account_center.purchase_fuel_packages_note') }}</small>
              <el-icon class="fuel-chevron" :class="{ expanded: fuelInlineVisible }"><ArrowRight /></el-icon>
            </button>
            <div v-if="fuelInlineVisible" class="fuel-packages-grid">
              <template v-if="fuelPackagesLoading">
                <el-skeleton v-for="item in 3" :key="item" animated class="fuel-package-skeleton"><template #template><el-skeleton-item variant="h3" /><el-skeleton-item variant="text" /><el-skeleton-item variant="button" /></template></el-skeleton>
              </template>
              <template v-else-if="fuelPackages.length">
                <article v-for="pkg in fuelPackages" :key="pkg.id" class="fuel-package-card">
                  <el-tag v-if="pkg.is_recommend === 1" size="small" type="primary" effect="light">{{ $t('views.account_center.recommended') }}</el-tag>
                  <div class="fuel-package-title"><el-icon><Coin /></el-icon>{{ pkg.package_name }}</div>
                  <p>{{ pkg.description }}</p>
                  <strong>{{ formatPackagePrice(pkg.price, pkg.currency) }}</strong>
                  <el-button type="primary" :disabled="isDomestic && paymentChannelsLoading" :loading="fuelPurchaseLoading === pkg.id" @click="purchaseFuelPackage(pkg)">{{ $t('views.account_center.buy_now') }}</el-button>
                </article>
              </template>
              <el-empty v-else :description="$t('views.account_center.purchase_no_fuel_packages')" />
            </div>
          </section>

        </div>
      </div>

      <section v-else class="membership-empty-state">
        <p>{{ $t('views.account_center.purchase_no_matching_plan') }}</p>
        <el-button type="primary" @click="openFuelPackagesDialog"><el-icon><Coin /></el-icon>{{ $t('views.account_center.purchase_fuel_packages') }}</el-button>
      </section>
    </section>

    <el-dialog
      v-model="fuelPackagesDialogVisible"
      class="fuel-packages-dialog"
      :class="{ 'is-compact': !fuelPackagesLoading && fuelPackages.length <= 1 }"
      :title="$t('views.account_center.purchase_fuel_packages')"
      width="620px"
      append-to-body
      destroy-on-close
    >
      <div class="fuel-packages-grid">
        <template v-if="fuelPackagesLoading">
          <el-skeleton
            v-for="item in 2"
            :key="item"
            animated
            class="fuel-package-skeleton"
          >
            <template #template>
              <el-skeleton-item variant="h3" />
              <el-skeleton-item variant="text" />
              <el-skeleton-item variant="button" />
            </template>
          </el-skeleton>
        </template>
        <template v-else-if="fuelPackages.length">
          <article v-for="pkg in fuelPackages" :key="pkg.id" class="fuel-package-card">
            <div class="fuel-package-heading">
              <div class="fuel-package-title"><el-icon><Coin /></el-icon><span>{{ pkg.package_name }}</span></div>
              <el-tag v-if="pkg.is_recommend === 1" size="small" type="primary" effect="light">{{ $t('views.account_center.recommended') }}</el-tag>
            </div>
            <p>{{ pkg.description }}</p>
            <strong>{{ formatPackagePrice(pkg.price, pkg.currency) }}</strong>
            <el-button
              type="primary"
              :disabled="isDomestic && paymentChannelsLoading"
              :loading="fuelPurchaseLoading === pkg.id"
              @click="purchaseFuelPackage(pkg)"
            >{{ $t('views.account_center.buy_now') }}</el-button>
          </article>
        </template>
        <el-empty v-else class="fuel-packages-empty" :description="$t('views.account_center.purchase_no_fuel_packages')" />
      </div>
    </el-dialog>

    </template>

      <component
        :is="isMobileView ? 'el-drawer' : 'el-dialog'"
        v-model="paymentDialogVisible"
        class="qr-payment-dialog"
        :width="isMobileView ? undefined : '420px'"
        :size="isMobileView ? 'min(92vh, 860px)' : undefined"
        :direction="isMobileView ? 'btt' : undefined"
        append-to-body
        :show-close="false"
        :close-on-click-modal="false"
        destroy-on-close
        @close="handlePaymentDialogClose"
      >
        <div class="qr-payment">
          <div class="qr-payment-header">
            <div>
              <h3>{{ $t('views.account_center.purchase_scan_title') }}</h3>
            </div>
            <el-button circle text :aria-label="$t('common.close')" @click="closePaymentDialog">
              <el-icon><Close /></el-icon>
            </el-button>
          </div>

          <div class="qr-product-line">
            <span>{{ paymentItemName }}</span>
            <strong>{{ paymentAmount }}</strong>
          </div>

          <div class="qr-payment-panel">
            <div
              class="qr-code-frame"
              :class="{ loading: qrLoading, 'alipay-web': isAlipayWebPayment }"
            >
              <div v-if="isAlipayWebPayment" class="alipay-checkout-viewport">
                <iframe
                  :src="paymentCheckoutUrl"
                  class="alipay-checkout-frame"
                  :title="$t('views.account_center.alipay')"
                  referrerpolicy="strict-origin-when-cross-origin"
                  scrolling="no"
                  @load="handleAlipayFrameLoaded"
                />
              </div>
              <template v-else>
                <img v-if="qrImage && !qrLoading" :src="qrImage" alt="QR Code">
                <div v-show="!qrImage && !qrLoading" ref="qrCode" class="qr-code-canvas" />
              </template>
              <div v-if="qrLoading" class="qr-loading">
                <el-icon class="is-loading"><Loading /></el-icon>
                <span>{{ $t('views.account_center.purchase_qr_loading') }}</span>
              </div>
            </div>

            <div
              class="payment-method-switch"
              :class="{ 'single-method': paymentMethods.length === 1 }"
              role="tablist"
              :aria-label="$t('views.account_center.purchase_payment_method')"
            >
              <button
                v-for="method in paymentMethods"
                :key="method.value"
                type="button"
                role="tab"
                :aria-selected="paymentChannel === method.value"
                :class="{ active: paymentChannel === method.value }"
                @click="setPaymentChannel(method.value)"
              >
                <span class="method-logo" :class="method.value">{{ method.short }}</span>
                <span>{{ method.label }}</span>
              </button>
            </div>

            <p class="qr-description">{{ $t('views.account_center.purchase_scan_description') }}</p>
            <p class="qr-agreement">{{ $t('views.account_center.purchase_agreement') }}</p>
          </div>

          <div class="qr-actions">
            <el-popover
              placement="top-start"
              :width="260"
              trigger="hover"
              :disabled="mobileHoverHintsDisabled"
              popper-class="invoice-help-popover"
            >
              <template #reference>
                <el-button>{{ $t('views.account_center.purchase_self_invoice') }}</el-button>
              </template>
              <div class="invoice-help-content">
                <strong>{{ $t('views.account_center.purchase_self_invoice') }}</strong>
                <p>{{ $t('views.account_center.purchase_invoice_help') }}</p>
              </div>
            </el-popover>
            <el-button type="primary" @click="checkPaymentNow">
              {{ $t('views.account_center.purchase_payment_done') }}
            </el-button>
          </div>
        </div>
      </component>

  </div>
</template>

<script>
import MobileSubscriptionPurchase from '@/views/mobile/account_center/components/MobileSubscriptionPurchase.vue'
import { useDisplayState } from '@/views/mobile/shared/useDisplayState'
import { ArrowLeft, ArrowRight, Close, Coin, Loading, Lock } from '@element-plus/icons-vue'
import QRCode from 'qrcodejs2'
import { allVerPacketData, createExpandPackageOrder, getExpandPackageList, getPointsDetails, getVerPriceDiff } from '@/api/team'
import { createCheckoutSession, getCurrSubscription, getPaymentMethodList } from '@/api/user'
import { getOrderInfo } from '@/api/order'
import { formatCurrency } from '@/utils/currency'
import { mobileHoverHintsDisabled } from '@/views/mobile/hoverHints'

const FREE_PLAN_KEY = 'VER_PERSON_FREE_BASICS'

export default {
  setup() { return useDisplayState() },
  name: 'SubscriptionPurchase',
  components: {
    MobileSubscriptionPurchase,
    ArrowLeft,
    ArrowRight,
    Close,
    Coin,
    Loading,
    Lock,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'payment-success'],
  data() {
    const mobileMediaQuery = window.matchMedia('(max-width: 768px)')
    const domestic = String(process.env.VUE_APP_LEGAL_REGION || '').toLowerCase() === 'domestic'
    return {
      mobileMediaQuery,
      isMobileView: this.displayState?.layoutMode === 'mobile',
      subscriptionActive: true,
      isDomestic: domestic,
      selectedPeriodType: 2,
      plans: [],
      currentSubscription: {},
      selectedPlan: null,
      periodScrollLeft: 0,
      periodScrollMax: 0,
      periodCardWidth: 160,
      periodResizeObserver: null,
      isLoading: true,
      isCreatingOrder: false,
      priceQuote: null,
      priceQuoteKey: '',
      priceQuoteLoading: false,
      priceQuoteError: false,
      priceQuoteRequestId: 0,
      priceQuoteDisposed: false,
      pointsData: {
        totalPoints: 0,
        subPoints: 0,
        subTotal: 0,
        buyPoints: 0,
        buyTotal: 0,
        actPoints: 0,
        actTotal: 0
      },
      fuelPackagesVisible: false,
      fuelInlineVisible: true,
      fuelPackagesDialogVisible: false,
      fuelPackages: [],
      fuelPackagesLoading: false,
      fuelPurchaseLoading: null,
      selectedFuelPackage: null,
      paymentContext: 'plan',
      paymentDialogVisible: false,
      availablePaymentChannels: [],
      paymentChannelsLoading: false,
      paymentChannel: '',
      paymentQr: '',
      qrImage: '',
      paymentActionType: '',
      paymentCheckoutUrl: '',
      paymentActionRequestId: 0,
      paymentQueryRequestId: null,
      qrLoading: false,
      paymentAmount: '',
      orderId: '',
      pollingInterval: null,
      pollingCount: 0,
    }
  },
  computed: {
    mobileHoverHintsDisabled() {
      return mobileHoverHintsDisabled.value
    },
    isPersonalTeam() {
      const currentTeam = this.$store.getters['team/currentTeam'] || {}
      const userCurrentTeam = this.$store.getters['user/current_team'] || {}
      const currentTeamInfo = this.$store.getters['team/currentTeamInfo'] || {}
      const team = [currentTeam, userCurrentTeam, currentTeamInfo]
        .find(item => item.team_type !== undefined && item.team_type !== null && item.team_type !== '' &&
          Number.isFinite(Number(item.team_type))) || {}
      return Number(team.team_type) > 0
    },
    canManageSubscription() {
      return this.$store.getters['team/canManageSubscription'] === true
    },
    showPriceQuote() {
      return !this.isDomestic && this.canManageSubscription && Boolean(this.selectedPricingPlan)
    },
    internationalQuoteKey() {
      if (!this.showPriceQuote || this.isLoading) return ''
      return [this.$store.getters['user/current_team_id'], this.$store.getters['user/user_id'], this.selectedPricingPlan.plan_id].join(':')
    },
    hasCurrentPriceQuote() {
      return Boolean(this.internationalQuoteKey && this.priceQuote && this.priceQuoteKey === this.internationalQuoteKey)
    },
    canSubmitPlanPurchase() {
      if (!this.canManageSubscription || !this.selectedPricingPlan || this.isCreatingOrder) return false
      return this.isDomestic
        ? !this.paymentChannelsLoading
        : this.hasCurrentPriceQuote && !this.priceQuoteLoading && !this.priceQuoteError
    },
    userAvatar() {
      return this.$store.getters['user/avatar'] || require('@/assets/img/def_user.png')
    },
    userNickname() {
      return this.$store.getters['user/nick_name'] || this.$t('views.account_center.purchase_account_fallback')
    },
    userEmail() {
      return this.$store.getters['user/email'] || ''
    },
    currentSubscriptionKey() {
      return this.$store.getters['user/user_sub'] || FREE_PLAN_KEY
    },
    isFreeMembership() {
      if (this.currentSubscription?.ver_id || this.currentSubscription?.plan_id ||
        this.periodMonths(Number(this.currentSubscription?.period_type))) return false
      if (this.isDomestic && this.currentPlanData) return Number(this.currentPlanData.is_free) === 1
      return this.currentSubscriptionKey === FREE_PLAN_KEY
    },
    currentPlanData() {
      const subscriptionVerId = this.currentSubscription?.ver_id
      if (subscriptionVerId) {
        return this.plans.find(plan => String(plan.ver_id) === String(subscriptionVerId)) || null
      }
      return this.plans.find(plan => {
        return [plan.ver_key, plan.key, plan.ver_code].filter(Boolean).includes(this.currentSubscriptionKey)
      }) || null
    },
    currentVersionNumber() {
      const currentVersionValue = this.currentPlanData?.ver_number
      const currentVersion = Number(currentVersionValue)
      if (currentVersionValue !== null && currentVersionValue !== undefined && currentVersionValue !== '' && Number.isFinite(currentVersion)) {
        return currentVersion
      }
      const subscriptionVersionValue = this.currentSubscription?.ver_number
      const subscriptionVersion = Number(subscriptionVersionValue)
      if (subscriptionVersionValue !== null && subscriptionVersionValue !== undefined && subscriptionVersionValue !== '' && Number.isFinite(subscriptionVersion)) return subscriptionVersion
      const currentPlanId = Number(this.currentSubscription?.plan_id)
      const currentPricing = this.currentPlanData?.pricing_plans?.find(item => Number(item.plan_id) === currentPlanId)
      if (currentPricing && Number.isFinite(Number(currentPricing.ver_number))) {
        return Number(currentPricing.ver_number)
      }
      const pricing = this.currentPlanData?.pricing_plans?.find(item => Number.isFinite(Number(item.ver_number)))
      return pricing ? Number(pricing.ver_number) : null
    },
    currentPeriodType() {
      const subscriptionPeriod = Number(this.currentSubscription?.period_type)
      if (this.periodMonths(subscriptionPeriod)) return subscriptionPeriod
      const selectablePeriods = (this.currentPlanData?.pricing_plans || [])
        .filter(item => item.is_select === 1)
        .map(item => Number(item.period_type))
        .filter(item => this.periodMonths(item))
      return selectablePeriods.length
        ? selectablePeriods.sort((a, b) => this.periodMonths(a) - this.periodMonths(b))[0]
        : 0
    },
    currentMembershipExpiresAt() {
      const value = this.currentSubscription?.expires_at ||
        this.currentSubscription?.ends_at ||
        this.currentSubscription?.next_billing_date
      return this.parseSubscriptionDate(value)
    },
    membershipExpiryText() {
      if (!this.currentMembershipExpiresAt) return ''
      return this.$t('views.account_center.membership_expires_on', {
        expiryDate: this.formatMembershipExpiry(this.currentMembershipExpiresAt)
      })
    },
    currentMembershipLabel() {
      if (this.isFreeMembership) {
        return this.$t('views.account_center.purchase_free_member')
      }
      return this.currentPlanData?.ver_name || this.$t('views.account_center.purchase_paid_member')
    },
    purchaseFootnote() {
      return this.isDomestic
        ? this.$t('views.account_center.purchase_domestic_footnote')
        : this.$t('views.account_center.purchase_international_footnote')
    },
    versionPlans() {
      return this.plans.filter(plan => !plan.is_free && this.availablePricingPlansFor(plan).length)
    },
    showPeriodNavigation() {
      return this.periodScrollMax > 1
    },
    canScrollPeriodsPrev() {
      return this.periodScrollLeft > 1
    },
    canScrollPeriodsNext() {
      return this.periodScrollLeft < this.periodScrollMax - 1
    },
    paymentMethods() {
      const methodMeta = {
        wechat: { value: 'wechat', label: this.$t('views.account_center.wechat'), short: '微' },
        alipay: { value: 'alipay', label: this.$t('views.account_center.alipay'), short: '支' }
      }
      return this.availablePaymentChannels
        .map(item => methodMeta[item.payment_channel])
        .filter(Boolean)
    },
    defaultDirectPaymentChannel() {
      if (this.paymentMethods.some(item => item.value === 'wechat')) return 'wechat'
      return this.paymentMethods[0]?.value || ''
    },
    periodType() {
      return Number(this.selectedPeriodType) || 2
    },
    availablePricingPlans() {
      return this.availablePricingPlansFor(this.selectedPlanData)
    },
    selectedPricingPlan() {
      return this.availablePricingPlans.find(item => Number(item.period_type) === Number(this.selectedPeriodType)) || this.availablePricingPlans[0] || null
    },
    isSelectedPlanUpgrade() {
      if (!this.isDomestic || this.isFreeMembership || !this.currentPlanData || !this.selectedPlanData) return false
      return Number(this.selectedPlanData.ver_id) !== Number(this.currentPlanData.ver_id) &&
        Number(this.selectedPlanData.ver_number) > Number(this.currentVersionNumber)
    },
    purchaseActionLabel() {
      if (!this.isDomestic) return this.$t('views.account_center.purchase_subscribe_now')
      if (this.isFreeMembership || !this.currentPlanData) return this.$t('views.account_center.purchase_buy_now')
      return this.isSelectedPlanUpgrade
        ? this.$t('views.account_center.purchase_upgrade_now')
        : this.$t('views.account_center.purchase_renew_now')
    },
    selectedPlanData() {
      return this.plans.find(item => item.ver_id === this.selectedPlan) || null
    },
    selectedPlanDisplayName() {
      return this.selectedPlanData?.ver_name || ''
    },
    paymentItemName() {
      return this.paymentContext === 'fuel'
        ? (this.selectedFuelPackage?.package_name || '')
        : this.selectedPlanDisplayName
    },
    isAlipayWebPayment() {
      return this.paymentChannel === 'alipay' &&
        this.paymentActionType === 'iframe' &&
        Boolean(this.paymentCheckoutUrl)
    }
  },
  watch: {
    'displayState.layoutMode'() {
      this.handleMobileViewportChange()
    },
    internationalQuoteKey() {
      this.fetchPriceQuote()
    }
  },
  mounted() {
    this.mobileMediaQuery.addEventListener('change', this.handleMobileViewportChange)
    window.addEventListener('resize', this.updatePeriodCarousel)
    this.fetchPlans()
    this.fetchPointsDetails()
    if (!this.isMobileView) this.fetchFuelPackages()
    if (this.isDomestic) this.fetchPaymentChannels()
  },
  beforeUnmount() {
    this.subscriptionActive = false
    this.mobileMediaQuery.removeEventListener('change', this.handleMobileViewportChange)
    this.priceQuoteDisposed = true
    this.priceQuoteRequestId += 1
    window.removeEventListener('resize', this.updatePeriodCarousel)
    if (this.periodResizeObserver) this.periodResizeObserver.disconnect()
    this.resetPaymentDialog()
  },
  methods: {
    getPeriodViewport() {
      return this.isMobileView ? null : this.$refs.periodViewport
    },
    handleMobileViewportChange() {
      const next = this.displayState?.layoutMode === 'mobile'
      if (next === this.isMobileView) return
      const scrollLeft = this.getPeriodViewport()?.scrollLeft ?? this.periodScrollLeft
      this.periodScrollLeft = scrollLeft
      this.isMobileView = next
      if (!next && this.fuelInlineVisible && !this.fuelPackages.length) this.fetchFuelPackages()
      this.$nextTick(() => {
        if (this.isMobileView !== next) return
        this.observePeriodViewport()
        const viewport = this.getPeriodViewport()
        if (viewport) viewport.scrollLeft = scrollLeft
        this.updatePeriodCarousel()
      })
    },
    async fetchPriceQuote() {
      const requestId = ++this.priceQuoteRequestId
      const quoteKey = this.internationalQuoteKey
      this.priceQuote = null
      this.priceQuoteKey = ''
      this.priceQuoteError = false
      this.priceQuoteLoading = Boolean(quoteKey)
      if (!quoteKey || this.priceQuoteDisposed) return

      const pricing = this.selectedPricingPlan
      try {
        const response = await getVerPriceDiff({ plan_id: pricing.plan_id, payment_channel: 'stripe' })
        if (this.priceQuoteDisposed || requestId !== this.priceQuoteRequestId || quoteKey !== this.internationalQuoteKey) return
        const quote = response?.data?.new_plan
        const price = quote?.plan?.price
        const amount = quote?.amount
        const discount = quote?.discount_amount
        const currency = quote?.plan?.currency || pricing.currency
        if ([price, amount, discount].some(value => value === null || value === undefined || value === '' || !Number.isFinite(Number(value)) || Number(value) < 0) ||
          !currency || String(currency).toUpperCase() !== String(pricing.currency).toUpperCase() ||
          String(quote?.plan?.id) !== String(pricing.plan_id)) {
          throw new Error('Invalid subscription price quote')
        }
        this.priceQuote = { price, amount, discount, currency }
        this.priceQuoteKey = quoteKey
      } catch (error) {
        if (this.priceQuoteDisposed || requestId !== this.priceQuoteRequestId || quoteKey !== this.internationalQuoteKey) return
        this.priceQuoteError = true
      } finally {
        if (!this.priceQuoteDisposed && requestId === this.priceQuoteRequestId) this.priceQuoteLoading = false
      }
    },
    fetchPaymentChannels() {
      this.paymentChannelsLoading = true
      getPaymentMethodList()
        .then(response => {
          this.availablePaymentChannels = response?.data?.channels || []
          this.paymentChannel = this.defaultDirectPaymentChannel
        })
        .catch(error => {
          console.error(this.$t('views.account_center.purchase_payment_channels_failed'), error)
          this.availablePaymentChannels = []
        })
        .finally(() => {
          this.paymentChannelsLoading = false
        })
    },
    fetchPlans() {
      this.isLoading = true
      const subscriptionRequest = getCurrSubscription()
        .catch(error => {
          console.error('Failed to load current subscription', error)
          return { data: {} }
        })
      Promise.all([allVerPacketData(), subscriptionRequest])
        .then(([plansResponse, subscriptionResponse]) => {
          this.plans = plansResponse?.data || []
          const subscription = subscriptionResponse?.data
          this.currentSubscription = subscription && !Array.isArray(subscription) ? subscription : {}
          this.selectDefaultPlan()
        })
        .catch(error => {
          console.error(this.$t('views.account_center.get_plan_id_failed'), error)
          this.$message.error(this.$t('views.account_center.get_plan_id_failed'))
        })
        .finally(() => {
          this.isLoading = false
          this.$nextTick(() => {
            this.observePeriodViewport()
            this.updatePeriodCarousel()
          })
        })
    },
    selectDefaultPlan() {
      this.selectedPlan = this.versionPlans[0]?.ver_id || null
      const pricing = this.availablePricingPlansFor(this.selectedPlanData)
      const recommended = pricing.find(item => item.recommend === 1)
      this.selectedPeriodType = Number(recommended?.period_type || pricing[0]?.period_type || 2)
    },
    getPricingPlan(plan) {
      return this.availablePricingPlansFor(plan).find(item => Number(item.period_type) === Number(this.periodType)) || null
    },
    availablePricingPlansFor(plan) {
      if (!plan?.pricing_plans) return []
      return plan.pricing_plans
        .filter(item => {
          // International Stripe keeps the legacy monthly/yearly catalog.
          if (!this.isDomestic && ![1, 2].includes(Number(item.period_type))) return false
          return item.is_select === 1 && this.isPricingPlanVisible(plan, item)
        })
        .sort((a, b) => this.periodMonths(a.period_type) - this.periodMonths(b.period_type))
    },
    isPricingPlanVisible(plan, pricing) {
      if (!pricing) return false
      if (this.isFreeMembership) return true
      if (!this.currentPlanData) return false

      const targetPeriodType = Number(pricing.period_type)
      const sameVersion = String(plan.ver_id) === String(this.currentPlanData.ver_id)
      const targetVersionNumber = Number(plan.ver_number)

      if (!this.isDomestic) {
        if (!Number.isFinite(targetVersionNumber) || !Number.isFinite(this.currentVersionNumber)) return false
        if (this.currentPeriodType && this.periodMonths(targetPeriodType) < this.periodMonths(this.currentPeriodType)) return false
        return (sameVersion && this.periodMonths(targetPeriodType) > this.periodMonths(this.currentPeriodType)) ||
          (!sameVersion && targetVersionNumber > this.currentVersionNumber)
      }

      if (sameVersion) return true

      if (this.currentPeriodType && this.periodMonths(targetPeriodType) < this.periodMonths(this.currentPeriodType)) return false

      if (!Number.isFinite(targetVersionNumber) ||
        !Number.isFinite(this.currentVersionNumber) ||
        !this.currentMembershipExpiresAt) return false

      const targetPeriodEnd = this.addCalendarPeriod(new Date(), targetPeriodType)
      return targetPeriodEnd.getTime() > this.currentMembershipExpiresAt.getTime() &&
        targetVersionNumber > this.currentVersionNumber
    },
    periodMonths(periodType) {
      return {
        1: 1,
        2: 12,
        3: 3,
        4: 6,
        5: 24
      }[Number(periodType)] || 0
    },
    periodName(periodType) {
      return this.$t({
        1: 'views.account_center.purchase_period_month',
        2: 'views.account_center.purchase_period_year',
        3: 'views.account_center.purchase_period_quarter',
        4: 'views.account_center.purchase_period_half_year',
        5: 'views.account_center.purchase_period_two_year'
      }[Number(periodType)] || 'views.account_center.purchase_period_month')
    },
    parseSubscriptionDate(value) {
      if (!value) return null
      const dateMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
      const date = dateMatch
        ? new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]))
        : new Date(value)
      if (Number.isNaN(date.getTime())) return null
      date.setHours(0, 0, 0, 0)
      return date
    },
    addCalendarPeriod(value, periodType) {
      const date = new Date(value)
      const originalDay = date.getDate()
      date.setDate(1)
      date.setMonth(date.getMonth() + this.periodMonths(periodType))
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
      date.setDate(Math.min(originalDay, lastDay))
      return date
    },
    formatMembershipExpiry(value) {
      const date = value instanceof Date ? value : this.parseSubscriptionDate(value)
      if (!date || Number.isNaN(date.getTime())) return ''
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${date.getFullYear()}.${month}.${day}`
    },
    getPlanPrice(plan) {
      const pricing = this.getPricingPlan(plan)
      return pricing ? formatCurrency(pricing.price, pricing.currency) : '--'
    },
    getPricingSubline(pricing) {
      const months = this.periodMonths(pricing?.period_type)
      if (months > 1 && Number.isFinite(Number(pricing?.price))) {
        const monthly = Number(pricing.price) / months
        return `${this.$t('views.account_center.purchase_monthly_equivalent')} ${formatCurrency(monthly.toFixed(2), pricing.currency)}`
      }
      return this.isDomestic ? this.$t('views.account_center.purchase_one_time') : this.$t('views.account_center.purchase_auto_renew')
    },
    visibleFeatures(plan) {
      const features = plan?.present_benefits || []
      const visible = this.isPersonalTeam
        ? features.filter(feature => !this.isTeamMemberBenefit(feature))
        : features
      return visible.slice(0, 8)
    },
    isTeamMemberBenefit(feature) {
      const value = String(feature || '').trim()
      return /^(?:团队|團隊)\s*\d+\s*人$/.test(value) ||
        /^\d[\d,]*\s+Team Members?$/i.test(value)
    },
    hasRecommendedPlan(plan) {
      return this.availablePricingPlansFor(plan).some(item => item.recommend === 1)
    },
    selectPlan(planId) {
      this.selectedPlan = planId
      const pricing = this.availablePricingPlans
      const recommended = pricing.find(item => item.recommend === 1)
      this.selectedPeriodType = Number(recommended?.period_type || pricing[0]?.period_type || 2)
      this.closePaymentDialog()
      this.$nextTick(() => {
        this.resetPeriodCarousel()
        this.observePeriodViewport()
        this.updatePeriodCarousel()
      })
    },
    setPeriodType(periodType) {
      this.selectedPeriodType = Number(periodType)
      this.closePaymentDialog()
    },
    updatePeriodCarousel() {
      const viewport = this.getPeriodViewport()
      if (!viewport) {
        // The phone uses a grid; retain the desktop carousel offset until it returns.
        if (!this.isMobileView) {
          this.periodScrollLeft = 0
          this.periodScrollMax = 0
        }
        return
      }

      const gap = 10
      const viewportWidth = viewport.clientWidth
      if (!viewportWidth) return

      this.periodCardWidth = this.calculatePeriodCardWidth(viewportWidth, gap)
      this.periodScrollLeft = viewport.scrollLeft
      this.$nextTick(() => {
        if (this.getPeriodViewport() !== viewport) return
        this.periodScrollMax = Math.max(0, viewport.scrollWidth - viewport.clientWidth)
      })
    },
    calculatePeriodCardWidth(viewportWidth, gap = 10) {
      const preferredVisibleCount = this.embedded ? 3 : 4
      const minCardWidth = 136
      const widthBasedCount = Math.floor((viewportWidth + gap) / (minCardWidth + gap))
      const visibleCount = Math.max(
        1,
        Math.min(
          this.availablePricingPlans.length || 1,
          preferredVisibleCount,
          widthBasedCount || 1
        )
      )
      return Math.min(160, Math.floor((viewportWidth - gap * (visibleCount - 1)) / visibleCount))
    },
    observePeriodViewport() {
      if (this.periodResizeObserver) this.periodResizeObserver.disconnect()
      if (typeof ResizeObserver === 'undefined' || !this.getPeriodViewport()) return
      this.periodResizeObserver = new ResizeObserver(() => this.updatePeriodCarousel())
      this.periodResizeObserver.observe(this.getPeriodViewport())
    },
    handlePeriodScroll(event) {
      this.periodScrollLeft = event.target.scrollLeft
    },
    scrollPeriods(direction) {
      const viewport = this.getPeriodViewport()
      if (!viewport) return

      const firstCard = viewport.querySelector('.period-card')
      const cardWidth = firstCard?.getBoundingClientRect().width || this.periodCardWidth || 160
      const styles = firstCard ? window.getComputedStyle(firstCard.parentElement) : null
      const gap = styles ? parseFloat(styles.columnGap || styles.gap || 10) : 10
      const distance = Math.max(viewport.clientWidth - gap, cardWidth + gap)
      const target = Math.max(0, Math.min(viewport.scrollLeft + direction * distance, this.periodScrollMax))
      viewport.scrollTo({ left: target, behavior: 'smooth' })
    },
    resetPeriodCarousel() {
      const viewport = this.getPeriodViewport()
      if (viewport) viewport.scrollLeft = 0
      this.periodScrollLeft = 0
      this.periodScrollMax = 0
    },
    close() {
      this.closePaymentDialog()
      this.$emit('close')
    },
    handlePlanPurchase(plan) {
      if (!this.subscriptionActive || this.isCreatingOrder || this.fuelPurchaseLoading !== null || this.qrLoading) return
      if (!this.canManageSubscription) {
        this.$message.warning(this.$t('views.account_center.purchase_owner_only'))
        return
      }
      if (!this.canSubmitPlanPurchase) return
      const pricing = this.selectedPricingPlan || this.getPricingPlan(plan)
      if (!plan || !pricing?.plan_id) {
        this.$message.warning(this.$t('views.account_center.select_plan_prompt'))
        return
      }

      this.selectedPlan = plan.ver_id
      this.selectedFuelPackage = null
      this.paymentContext = 'plan'

      if (this.isDomestic) {
        if (!this.defaultDirectPaymentChannel) {
          this.$message.warning(this.$t('views.account_center.purchase_no_payment_channel'))
          return
        }
        this.paymentChannel = this.defaultDirectPaymentChannel
        // Upgrade pricing is calculated server-side from the current
        // subscription. Do not show the target plan's full price while the
        // prorated amount is being prepared.
        this.paymentAmount = this.isSelectedPlanUpgrade
          ? '--'
          : this.formatPackagePrice(pricing.price, pricing.currency)
        this.paymentDialogVisible = true
        this.loadDomesticPaymentAction(this.paymentChannel)
        return
      }

      this.createInternationalCheckout(pricing.plan_id)
    },
    fetchPointsDetails() {
      getPointsDetails()
        .then(response => {
          const data = response?.data || {}
          this.pointsData = {
            totalPoints: data.total_surplus || 0,
            subTotal: data.plan_points?.total || 0,
            subPoints: data.plan_points?.used || 0,
            buyTotal: data.expand_points?.total || 0,
            buyPoints: data.expand_points?.used || 0,
            actTotal: data.activity_points?.total || 0,
            actPoints: data.activity_points?.used || 0
          }
        })
        .catch(error => console.error(this.$t('views.account_center.get_points_info_failed'), error))
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString()
    },
    formatPointRange(value, total) {
      return `${this.formatNumber(value)}/${this.formatNumber(total)}`
    },
    formatPackagePrice(price, currency) {
      return formatCurrency(price, currency)
    },
    openFuelPackages() {
      this.fuelPackagesVisible = !this.fuelPackagesVisible
      if (this.fuelPackagesVisible && !this.fuelPackages.length) this.fetchFuelPackages()
    },
    toggleFuelInline() {
      this.fuelInlineVisible = !this.fuelInlineVisible
      if (this.fuelInlineVisible && !this.fuelPackages.length) this.fetchFuelPackages()
    },
    openFuelPackagesDialog() {
      this.fuelPackagesDialogVisible = true
      if (!this.fuelPackages.length) this.fetchFuelPackages()
    },
    async fetchFuelPackages() {
      if (!this.subscriptionActive || this.fuelPackagesLoading) return
      this.fuelPackagesLoading = true
      try {
        const response = await getExpandPackageList({ page: 1, page_size: 99, is_pagination: 1 })
        if (!this.subscriptionActive) return
        const data = response?.data
        this.fuelPackages = Array.isArray(data) ? data : (data?.data || [])
      } catch (error) {
        if (this.subscriptionActive) {
          console.error(this.$t('views.account_center.get_package_list_failed'), error)
          this.$message.error(this.$t('views.account_center.get_package_list_failed'))
        }
      } finally {
        this.fuelPackagesLoading = false
      }
    },
    async purchaseFuelPackage(pkg) {
      if (!this.subscriptionActive || this.isCreatingOrder || this.fuelPurchaseLoading !== null || this.qrLoading || !pkg?.id) return
      if (this.isDomestic) {
        if (!this.defaultDirectPaymentChannel) {
          this.$message.warning(this.$t('views.account_center.purchase_no_payment_channel'))
          return
        }
        this.selectedFuelPackage = pkg
        this.paymentContext = 'fuel'
        this.paymentChannel = this.defaultDirectPaymentChannel
        this.paymentAmount = this.formatPackagePrice(pkg.price, pkg.currency)
        this.fuelPackagesVisible = false
        this.fuelPackagesDialogVisible = false
        this.paymentDialogVisible = true
        this.loadDomesticPaymentAction(this.paymentChannel)
        return
      }

      const requestId = ++this.paymentActionRequestId
      this.fuelPackagesDialogVisible = false
      this.fuelPurchaseLoading = pkg.id
      try {
        const response = await createExpandPackageOrder({ expand_id: pkg.id, payment_channel: 'stripe' })
        if (requestId === this.paymentActionRequestId) this.handlePaymentAction(response, 'stripe')
      } catch (error) {
        if (requestId === this.paymentActionRequestId) this.handlePaymentError(error)
      } finally {
        this.fuelPurchaseLoading = null
      }
    },
    async createInternationalCheckout(planId) {
      if (!this.subscriptionActive || this.isCreatingOrder || this.fuelPurchaseLoading !== null || this.qrLoading) return
      if (!this.canSubmitPlanPurchase || Number(planId) !== Number(this.selectedPricingPlan?.plan_id)) return
      const requestId = ++this.paymentActionRequestId
      this.isCreatingOrder = true
      try {
        const response = await createCheckoutSession({ plan_id: planId, payment_channel: 'stripe' })
        if (requestId === this.paymentActionRequestId) this.handlePaymentAction(response, 'stripe')
      } catch (error) {
        if (requestId === this.paymentActionRequestId) this.handlePaymentError(error)
      } finally {
        this.isCreatingOrder = false
      }
    },
    async loadDomesticPaymentAction(channel) {
      if (!this.subscriptionActive) return
      const isFuel = this.paymentContext === 'fuel'
      const pricing = isFuel ? null : (this.selectedPricingPlan || this.getPricingPlan(this.selectedPlanData))
      if (isFuel ? !this.selectedFuelPackage?.id : !pricing?.plan_id) return
      const requestId = ++this.paymentActionRequestId
      this.stopPolling()
      this.qrLoading = true
      this.paymentQr = ''
      this.qrImage = ''
      this.paymentActionType = ''
      this.paymentCheckoutUrl = ''
      this.orderId = ''
      try {
        const response = await (isFuel
          ? createExpandPackageOrder({ expand_id: this.selectedFuelPackage.id, payment_channel: channel })
          : createCheckoutSession({ plan_id: pricing.plan_id, payment_channel: channel }))
        if (requestId !== this.paymentActionRequestId) return
        this.handlePaymentAction(response, channel)
      } catch (error) {
        if (requestId !== this.paymentActionRequestId) return
        this.handlePaymentError(error)
      } finally {
        if (requestId === this.paymentActionRequestId && !this.isAlipayWebPayment) {
          this.qrLoading = false
          this.$nextTick(() => {
            if (this.subscriptionActive && requestId === this.paymentActionRequestId) this.renderQRCode()
          })
        }
      }
    },
    handlePaymentAction(response, requestedChannel) {
      if (!this.subscriptionActive) return
      const payload = response?.data || {}
      const order = payload.order || {}
      const payment = payload.payment || {}
      const actionData = payment.action_data || {}
      const rawActionType = payment.action_type || (payload.checkout_url ? 'redirect' : 'none')
      const isPendingSubscriptionOrder = payload.status === 'pending' &&
        payment.payment_scene === 'subscription' &&
        [3, 4].includes(Number(order.order_type))
      const hasRedirectUrl = Boolean(actionData.launch_url || actionData.checkout_url || payload.checkout_url)
      const needsPollingFallback = rawActionType === 'none' || (rawActionType === 'redirect' && !hasRedirectUrl)
      const actionType = needsPollingFallback && isPendingSubscriptionOrder ? 'polling' : rawActionType
      const channel = payment.payment_channel || requestedChannel

      this.orderId = order.order_no || payload.order_no || ''
      this.paymentChannel = channel || this.paymentChannel
      this.paymentActionType = actionType

      // Domestic upgrade orders return the prorated amount on the order,
      // not on the payment payload. Keep the pre-request plan price as a
      // fallback, then replace it with the amount that will actually be paid.
      if (order.amount !== undefined && order.amount !== null) {
        const fallbackCurrency = this.paymentContext === 'fuel'
          ? this.selectedFuelPackage?.currency
          : this.selectedPricingPlan?.currency
        this.paymentAmount = this.formatPackagePrice(
          order.amount,
          order.currency || fallbackCurrency
        )
      }

      if (actionType === 'qr_code') {
        if (!actionData.code_url) throw new Error('payment.action_data.code_url is empty')
        this.paymentCheckoutUrl = ''
        this.showPaymentCode(actionData.code_url)
        this.startPolling()
        return
      }

      if (actionType === 'iframe') {
        const checkoutUrl = actionData.checkout_url || payload.checkout_url
        if (!checkoutUrl) throw new Error('payment iframe URL is empty')
        this.paymentQr = ''
        this.qrImage = ''
        this.paymentCheckoutUrl = checkoutUrl
        this.startPolling()
        return
      }

      if (actionType === 'redirect') {
        const redirectUrl = actionData.launch_url || actionData.checkout_url || payload.checkout_url
        if (!redirectUrl) throw new Error('payment redirect URL is empty')
        window.location.assign(redirectUrl)
        return
      }

      if (actionType === 'polling') {
        this.startPolling()
        return
      }

      if (actionType === 'none' && payload.status === 'success') {
        if (this.orderId) this.queryOrderInfo()
        return
      }

      throw new Error(`Unsupported payment action: ${actionType}`)
    },
    setPaymentChannel(channel) {
      if (channel === this.paymentChannel) return
      this.paymentChannel = channel
      this.loadDomesticPaymentAction(channel)
    },
    showPaymentCode(code) {
      this.paymentCheckoutUrl = ''
      this.paymentQr = code || ''
      this.qrImage = this.isImageUrl(this.paymentQr) ? this.paymentQr : ''
      this.$nextTick(() => this.renderQRCode())
    },
    isImageUrl(value) {
      return typeof value === 'string' && (/^data:image\//.test(value) || /\.(png|jpg|jpeg|webp)(\?|$)/i.test(value))
    },
    renderQRCode() {
      if (!this.paymentQr || this.qrImage || !this.$refs.qrCode) return
      this.$refs.qrCode.innerHTML = ''
      new QRCode(this.$refs.qrCode, {
        text: this.paymentQr,
        width: 204,
        height: 204,
        correctLevel: QRCode.CorrectLevel.M
      })
    },
    handleAlipayFrameLoaded() {
      if (this.isAlipayWebPayment) this.qrLoading = false
    },
    startPolling() {
      this.stopPolling()
      if (!this.subscriptionActive || !this.orderId) return
      this.pollingCount = 0
      this.pollingInterval = setInterval(() => {
        this.pollingCount += 1
        if (this.pollingCount > 120) {
          this.stopPolling()
          return
        }
        this.queryOrderInfo()
      }, 1000)
    },
    stopPolling() {
      if (this.pollingInterval) clearInterval(this.pollingInterval)
      this.pollingInterval = null
    },
    checkPaymentNow() {
      if (this.orderId) this.queryOrderInfo(true)
    },
    async queryOrderInfo(showPendingMessage = false) {
      const requestId = this.paymentActionRequestId
      const orderId = this.orderId
      if (!this.subscriptionActive || !orderId || this.paymentQueryRequestId === requestId) return
      this.paymentQueryRequestId = requestId
      try {
        const response = await getOrderInfo({ order_no: orderId })
        // 关闭、换通道或离页后，旧订单查询不能发出支付成功事件。
        if (requestId !== this.paymentActionRequestId || orderId !== this.orderId) return
        const data = response?.data
        if (data?.order_status === 1) {
          this.stopPolling()
          this.paymentActionRequestId += 1
          this.orderId = ''
          this.paymentDialogVisible = false
          const orderType = data.order_type || (this.paymentContext === 'fuel' ? 2 : 1)
          const successDialogData = {
            ver_name: data.ver_name || this.selectedPlanDisplayName,
            present_benefits: data.ver?.present_benefits || this.selectedPlanData?.present_benefits || [],
            order_type: orderType,
            expand_data: data.expand || {
              package_name: this.selectedFuelPackage?.package_name || '',
              description: this.selectedFuelPackage?.description || ''
            }
          }

          if (orderType === 2) this.fetchPointsDetails()
          this.$emit('payment-success', {
            order: data,
            dialogData: successDialogData
          })
        } else if (data?.order_status === 2) {
          this.stopPolling()
          this.$message.warning(this.$t('views.account_center.order_cancelled'))
        } else if (showPendingMessage) {
          this.$message.info(this.$t('views.account_center.purchase_payment_pending'))
        }
      } catch (error) {
        if (requestId === this.paymentActionRequestId && showPendingMessage) this.handlePaymentError(error)
      } finally {
        if (this.paymentQueryRequestId === requestId) this.paymentQueryRequestId = null
      }
    },
    handlePaymentError(error) {
      console.error(this.$t('views.account_center.payment_error'), error)
      this.$message.error(this.$t('views.account_center.payment_error'))
    },
    resetPaymentDialog() {
      this.paymentActionRequestId += 1
      this.paymentQueryRequestId = null
      this.paymentActionType = ''
      this.paymentCheckoutUrl = ''
      this.paymentQr = ''
      this.qrImage = ''
      this.qrLoading = false
      this.orderId = ''
      this.stopPolling()
    },
    handlePaymentDialogClose() {
      if (!this.paymentDialogVisible) this.resetPaymentDialog()
    },
    closePaymentDialog() {
      this.paymentDialogVisible = false
      this.resetPaymentDialog()
    },
  }
}
</script>

<style scoped lang="scss">
@use '@/views/mobile/account_center/styles/subscription_purchase' as mobile;
@import '@/assets/css/views/account_center/components/subscription_purchase';
@include mobile.frame;
</style>

<style lang="scss">
@use '@/views/mobile/account_center/styles/payment_dialogs' as mobileDialogs;
.qr-payment-dialog {
  max-width: calc(100vw - 28px);
  overflow: hidden;
  border-radius: 8px;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
  }

  .qr-payment {
    --qr-text: var(--agst-text-color-primary, #303133);
    --qr-muted: var(--agst-text-color-secondary, #909399);
    --qr-border: var(--agst-border-color-lighter, #ebeef5);
    padding: 24px;
    color: var(--qr-text);
    background: var(--agst-bg-color, #fff);
  }

  .qr-payment-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;

    h3 {
      margin: 0;
      font-size: 20px;
      line-height: 1.4;
    }
  }

  .qr-product-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 42px;
    margin-top: 10px;
    padding: 0;

    span {
      max-width: 52%;
      overflow: hidden;
      color: var(--qr-muted);
      font-size: 13px;
      line-height: 1.45;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    strong {
      color: var(--agst-color-danger, #e71e1e);
      font-size: 24px;
      font-weight: 600;
      line-height: 1;
      white-space: nowrap;
    }
  }

  .qr-payment-panel {
    margin-top: 10px;
    padding: 14px 14px 12px;
    border-radius: 8px;
    background: var(--agst-fill-color-light, #f5f7fa);
  }

  .qr-code-frame {
    position: relative;
    display: grid;
    width: 224px;
    height: 224px;
    margin: 0 auto 14px;
    padding: 10px;
    place-items: center;
    border: 1px solid var(--qr-border);
    border-radius: 8px;
    background: #fff;

    &.loading {
      background: var(--agst-fill-color-extra-light, #fafcff);
    }

    &.alipay-web {
      padding: 9px;
    }

    img,
    .qr-code-canvas {
      display: block;
      width: 204px;
      height: 204px;
    }

    img {
      object-fit: contain;
    }
  }

  .alipay-checkout-viewport {
    display: block;
    width: 204px;
    height: 204px;
    flex: 0 0 204px;
    overflow: hidden;
    box-sizing: border-box;
  }

  .alipay-checkout-frame {
    display: block;
    width: 204px;
    height: 204px;
    min-width: 204px;
    min-height: 204px;
    border: 0;
    background: #fff;
    overflow: hidden;
    box-sizing: border-box;
  }

  .qr-loading {
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 8px;
    color: var(--qr-muted);
    font-size: 12px;

    .el-icon {
      color: var(--agst-color-primary, #2d66f0);
      font-size: 24px;
    }
  }

  .qr-code-frame .qr-loading {
    position: absolute;
    z-index: 1;
    inset: 10px;
    justify-content: center;
    background: rgba(255, 255, 255, 0.94);
  }

  .qr-code-frame.alipay-web .qr-loading {
    inset: 0;
  }

  .payment-method-switch {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 4px;
    padding: 4px;
    border-radius: 6px;
    background: var(--agst-fill-color-light, #f5f7fa);

    &.single-method {
      grid-template-columns: 1fr;
    }

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      height: 36px;
      border: 1px solid transparent;
      border-radius: 4px;
      color: var(--qr-muted);
      background: transparent;
      cursor: pointer;

      &.active {
        border-color: var(--agst-border-color, #dcdfe6);
        color: var(--qr-text);
        background: var(--agst-bg-color, #fff);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      }

      &:focus-visible {
        outline: 2px solid var(--agst-color-primary, #2d66f0);
        outline-offset: 1px;
      }
    }
  }

  .method-logo {
    display: inline-grid;
    width: 19px;
    height: 19px;
    place-items: center;
    border-radius: 4px;
    color: #fff;
    font-size: 11px;
    font-weight: 600;

    &.wechat { background: #1aad5b; }
    &.alipay { background: #1677ff; }
  }

  .qr-description,
  .qr-agreement {
    margin: 12px 0 0;
    color: var(--qr-muted);
    font-size: 12px;
    line-height: 1.55;
    text-align: center;
  }

  .qr-payment-panel .qr-description {
    overflow: hidden;
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .qr-agreement {
    margin-top: 5px;
    font-size: 11px;
  }

  .qr-actions {
    display: grid;
    grid-template-columns: 1fr 1.35fr;
    gap: 8px;
    margin-top: 18px;

    .el-button {
      width: 100%;
      margin: 0;
    }
  }
}

.invoice-help-popover {
  padding: 14px 16px !important;
  border-radius: 8px !important;

  .invoice-help-content {
    strong {
      display: block;
      color: var(--agst-text-color-primary, #303133);
      font-size: 14px;
      font-weight: 600;
      line-height: 1.5;
    }

    p {
      margin: 8px 0 0;
      color: var(--agst-text-color-regular, #606266);
      font-size: 13px;
      line-height: 1.65;
    }
  }
}

.fuel-packages-dialog {
  width: min(620px, calc(100vw - 32px));
  max-width: calc(100vw - 32px);
  border-radius: 8px;

  &.is-compact {
    width: min(440px, calc(100vw - 32px));
  }

  .el-dialog__header {
    padding: 24px 48px 16px;
    margin-right: 0;
    text-align: center;
  }

  .el-dialog__title {
    color: var(--agst-text-color-primary, #303133);
    font-size: 18px;
    font-weight: 600;
  }

  .el-dialog__body {
    max-height: min(620px, calc(100vh - 140px));
    padding: 4px 28px 28px;
    overflow-y: auto;
  }

  .fuel-packages-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  &.is-compact .fuel-packages-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .fuel-package-card,
  .fuel-package-skeleton {
    min-width: 0;
    min-height: 196px;
    padding: 20px;
    border: 1px solid var(--agst-border-color-lighter, #ebeef5);
    border-radius: 8px;
    background: var(--agst-bg-color, #fff);
  }

  .fuel-package-card {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .el-tag {
      flex: 0 0 auto;
    }

    p {
      flex: 1 0 auto;
      margin: 0;
      color: var(--agst-text-color-secondary, #909399);
      font-size: 13px;
      line-height: 1.6;
      overflow-wrap: anywhere;
    }

    > strong {
      color: var(--agst-text-color-primary, #303133);
      font-size: 24px;
      font-weight: 600;
    }

    > .el-button {
      width: 100%;
      min-height: 40px;
      margin-top: 2px;
    }
  }

  .fuel-package-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .fuel-package-title {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
    color: var(--agst-text-color-primary, #303133);
    font-size: 15px;
    font-weight: 600;
    line-height: 1.5;
    overflow-wrap: anywhere;

    .el-icon {
      flex: 0 0 auto;
      margin-top: 3px;
      color: var(--agst-color-primary, #2d66f0);
    }
  }

  .fuel-packages-empty {
    grid-column: 1 / -1;
    padding: 20px 0;
  }

  .fuel-package-skeleton {
    .el-skeleton__item {
      display: block;
      margin-bottom: 16px;
    }

    .el-skeleton__item:last-child {
      width: 100%;
      margin-top: 26px;
      margin-bottom: 0;
    }
  }


}

@include mobileDialogs.purchase;
@include mobileDialogs.qr;
</style>
